import { useState } from 'react';
import { useTranslation } from '@mukil/i18n';
import { Alert, Button, FileDropzone, GlassCard } from '@jumpstone/ui-react';
import {
  detectFormat,
  mergeDetectedSheets,
  parseUploads,
  type DetectedSheet,
  type MigrationSheetKind,
  type RawUpload,
} from './migration_upload_logic';

type TFn = (key: string, params?: Record<string, string | number>) => string;

/** Literal `t()` calls (not a template key) so the i18n scanner sees them. */
function sheetKindLabel(t: TFn, kind: MigrationSheetKind | null): string {
  if (kind === 'Employees') return t('migration.sheet.Employees');
  if (kind === 'Salary_History') return t('migration.sheet.Salary_History');
  if (kind === 'Payroll_History') return t('migration.sheet.Payroll_History');
  if (kind === 'Leave_Balances') return t('migration.sheet.Leave_Balances');
  return t('migration.sheet.file');
}

const ACCEPT = [
  'text/csv',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
];

async function toRawUpload(file: File): Promise<RawUpload | null> {
  const format = detectFormat(file.name);
  if (format === 'xlsx') {
    return {
      name: file.name,
      format,
      bytes: new Uint8Array(await file.arrayBuffer()),
    };
  }
  if (format === 'csv') {
    return { name: file.name, format, text: await file.text() };
  }
  return null;
}

/**
 * Step 3 — Upload: drop one workbook or several CSVs. Parsing + classification
 * is the pure `parseUploads`; this component only reads the Files and renders
 * the detected sheets.
 */
export function MigrationUploadStep({
  detectedSheets,
  onParsed,
  onBack,
  onNext,
}: {
  detectedSheets: DetectedSheet[];
  onParsed: (sheets: DetectedSheet[]) => void;
  onBack: () => void;
  onNext: () => void;
}): React.JSX.Element {
  const { t } = useTranslation('wp');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);

  async function handleFiles(files: File[]): Promise<void> {
    setBusy(true);
    setError(false);
    const uploads: RawUpload[] = [];
    for (const file of files) {
      const upload = await toRawUpload(file);
      if (upload) uploads.push(upload);
    }
    const result = await parseUploads(uploads);
    setBusy(false);
    if (result.outcome === 'failure') {
      setError(true);
      return;
    }
    // Accumulate across separate drops (one CSV per gesture) rather than
    // replacing the previous set (F6).
    onParsed(mergeDetectedSheets(detectedSheets, result.sheets));
  }

  const classified = detectedSheets.filter((s) => s.kind !== null);
  const hasEmployees = classified.some((s) => s.kind === 'Employees');

  return (
    <GlassCard className="rounded-2xl p-5">
      <div className="flex flex-col gap-4">
        <p className="text-sm text-[color:var(--ink-muted)]">
          {t('migration.upload.description')}
        </p>

        <FileDropzone
          multiple
          accept={ACCEPT}
          onFileSelect={() => undefined}
          onFilesSelect={(files) => {
            void handleFiles(files);
          }}
          disabled={busy}
          promptText={t('migration.upload.prompt')}
          subText={t('migration.upload.subtext')}
          browseText={t('migration.upload.browse')}
          unsupportedTypeError={() => t('migration.upload.parse_error')}
          tooLargeError={() => t('migration.error.file_too_large')}
          onError={() => {
            setError(true);
          }}
        />

        {error && (
          <Alert variant="danger" title={t('migration.upload.parse_error')} />
        )}

        {classified.length > 0 && (
          <div>
            <h3 className="mb-2 text-sm font-semibold">
              {t('migration.upload.detected')}
            </h3>
            <ul className="flex flex-col gap-1">
              {classified.map((sheet) => (
                <li
                  key={sheet.sourceName}
                  className="flex items-center justify-between rounded-lg border border-[color:var(--line)] px-3 py-2 text-sm"
                >
                  <span className="font-medium">{sheet.sourceName}</span>
                  <span className="text-xs text-[color:var(--ink-muted)]">
                    {sheetKindLabel(t, sheet.kind)} ·{' '}
                    <span className="num">
                      {Math.max(sheet.rawSheet.grid.length - 1, 0)}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
            {!hasEmployees && (
              <Alert className="mt-3" variant="warning">
                {t('migration.upload.need_employees')}
              </Alert>
            )}
          </div>
        )}

        <div className="flex justify-between">
          <Button variant="ghost" onClick={onBack}>
            {t('migration.button.back')}
          </Button>
          <Button disabled={!hasEmployees || busy} onClick={onNext}>
            {t('migration.button.next')}
          </Button>
        </div>
      </div>
    </GlassCard>
  );
}
